import type { StockStrategyParams, StockStrategyType } from './backtest'
import { clamp, mean, stdDev } from './statistics'
import { type GridEntry, paramAxes } from './optimize'

export interface SensitivityCell {
  x: number
  y: number
  metric: number
  trades: number
}

export interface SensitivitySummary {
  xKey: string
  yKey: string
  xValues: number[]
  yValues: number[]
  cells: SensitivityCell[]
  bestMetric: number
  neighborhoodMean: number
  peakRatio: number
  plateauFraction: number
  boundaryPeak: boolean
  fragilityScore: number
  robust: boolean
  verdict: string
}

function valueOf(params: StockStrategyParams, key: keyof StockStrategyParams): number {
  return params[key] as number
}

// Robust strategies sit on a broad plateau; overfit ones spike on a single isolated config.
export function parameterSensitivity(grid: readonly GridEntry[], type: StockStrategyType): SensitivitySummary {
  const axes = paramAxes(type)
  const xKey = axes.x.key
  const yKey = axes.y.key

  const cells: SensitivityCell[] = grid.map((g) => ({
    x: valueOf(g.params, xKey),
    y: valueOf(g.params, yKey),
    metric: Number.isFinite(g.metric) ? g.metric : 0,
    trades: g.metrics.trades,
  }))

  const finite = cells.filter((c) => Number.isFinite(c.metric))
  const metrics = finite.map((c) => c.metric)
  const bestMetric = metrics.length ? Math.max(...metrics) : 0
  const worstMetric = metrics.length ? Math.min(...metrics) : 0
  const range = Math.max(1e-9, bestMetric - worstMetric)

  const best = cells.reduce((a, b) => (b.metric > a.metric ? b : a), cells[0])

  const neighbors = cells.filter(
    (c) => c !== best && adjacent(c.x, best.x, axes.x.values) && adjacent(c.y, best.y, axes.y.values),
  )
  const neighborhoodMean = neighbors.length ? mean(neighbors.map((n) => n.metric)) : best.metric
  const peakRatio = range > 0 ? (best.metric - neighborhoodMean) / range : 0

  const threshold = bestMetric - 0.2 * range
  const plateauFraction = cells.length ? cells.filter((c) => c.metric >= threshold).length / cells.length : 0

  const boundaryPeak =
    best.x === axes.x.values[0] ||
    best.x === axes.x.values[axes.x.values.length - 1] ||
    best.y === axes.y.values[0] ||
    best.y === axes.y.values[axes.y.values.length - 1]

  const fragilityScore = clamp(
    0.5 * (1 - plateauFraction) + 0.35 * clamp(peakRatio, 0, 1) + 0.15 * (boundaryPeak ? 1 : 0),
    0,
    1,
  )
  const robust = fragilityScore < 0.45 && !boundaryPeak

  let verdict: string
  if (fragilityScore > 0.7) verdict = 'Fragile: performance collapses just off the best parameters — classic curve fit.'
  else if (fragilityScore > 0.45) verdict = 'Moderately sensitive: the edge depends on a fairly narrow parameter band.'
  else verdict = 'Robust: a broad plateau of parameters performs similarly. Good sign.'
  if (boundaryPeak) verdict += ' The optimum sits on the edge of the tested range — the true peak may be untested.'

  return {
    xKey: String(xKey),
    yKey: String(yKey),
    xValues: axes.x.values,
    yValues: axes.y.values,
    cells,
    bestMetric,
    neighborhoodMean,
    peakRatio,
    plateauFraction,
    boundaryPeak,
    fragilityScore,
    robust,
    verdict,
  }
}

function adjacent(value: number, center: number, axisValues: number[]): boolean {
  const vi = axisValues.indexOf(value)
  const ci = axisValues.indexOf(center)
  if (vi === -1 || ci === -1) return false
  return Math.abs(vi - ci) <= 1
}

export function metricDispersion(grid: readonly GridEntry[]): number {
  const metrics = grid.map((g) => (Number.isFinite(g.metric) ? g.metric : 0))
  return stdDev(metrics)
}
